import { useEffect, useState } from "react";
import { Plus, Pencil, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMessage } from "@/lib/api";
import { Loading, ErrorBox, Empty, FieldError } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { selectCls } from "@/pages/Take5New";
import { IDENT_KINDS } from "@/lib/labels";
import { MeasureFilters, Pager, PAGE_SIZE } from "@/components/admin/MeasureFilters";

const ListEditor = ({ value, onChange, testId }) => (
  <div className="space-y-2">
    {value.map((it, i) => (
      <div key={it.id || `n${i}`} className="flex gap-2">
        <Input data-testid={`${testId}-${i}`} value={it.label} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className="h-11" />
        <button type="button" aria-label="Retirer" onClick={() => onChange(value.filter((_, j) => j !== i))} className="flex h-11 w-11 items-center justify-center rounded-md text-slate-400 hover:bg-white/5"><Trash2 className="h-4 w-4" /></button>
      </div>
    ))}
    <Button type="button" variant="secondary" size="sm" data-testid={`${testId}-add`} onClick={() => onChange([...value, { id: null, label: "" }])}><Plus className="mr-1 h-4 w-4" /> Ajouter</Button>
  </div>
);

const IdentifierEditor = ({ value, onChange, testId }) => {
  const set = (i, patch) => onChange(value.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div className="space-y-2">
      {value.map((it, i) => (
        <div key={it.id || `n${i}`} className="grid grid-cols-[1fr_auto] gap-2 rounded-md border border-border p-2 sm:grid-cols-[170px_1fr_auto]">
          <select data-testid={`${testId}-kind-${i}`} className={`${selectCls} col-start-1 row-start-1 h-11`} value={it.kind} onChange={(e) => set(i, { kind: e.target.value })}>
            {IDENT_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
          <div className="col-span-2 flex flex-wrap gap-2 sm:col-span-1">
            {it.kind === "autre" && <Input data-testid={`${testId}-label-${i}`} placeholder="Libellé" value={it.label || ""} onChange={(e) => set(i, { label: e.target.value })} className="h-11 w-full" />}
            <Input data-testid={`${testId}-value-${i}`} placeholder="Valeur" value={it.value} onChange={(e) => set(i, { value: e.target.value })} className="h-11 w-full" />
          </div>
          <button type="button" aria-label="Retirer" data-testid={`${testId}-remove-${i}`} onClick={() => onChange(value.filter((_, j) => j !== i))} className="col-start-2 row-start-1 flex h-11 w-11 items-center justify-center rounded-md text-slate-400 hover:bg-white/5 sm:col-start-3"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <Button type="button" variant="secondary" size="sm" data-testid={`${testId}-add`} onClick={() => onChange([...value, { id: null, kind: "immatriculation", label: "", value: "" }])}><Plus className="mr-1 h-4 w-4" /> Ajouter un identifiant</Button>
    </div>
  );
};

export const YesNo = ({ value, onChange, testId }) => (
  <div className="flex gap-2">
    {["oui", "non"].map((v) => (
      <button key={v} type="button" data-testid={`${testId}-${v}`} aria-pressed={value === v} onClick={() => onChange(v)}
        className={`h-11 min-w-[90px] rounded-md border-2 px-4 font-bold transition-colors ${value === v ? "border-amber-500 bg-amber-500/15" : "border-border hover:border-slate-500"}`}>{v.toUpperCase()}</button>
    ))}
  </div>
);

const FieldInput = ({ f, value, onChange, options, row }) => {
  const id = `admin-field-${f.name}`;
  if (f.type === "bool") return (
    <label className="flex min-h-[44px] items-center gap-3"><input id={id} data-testid={id} type="checkbox" className="h-5 w-5 accent-amber-500" checked={!!value} onChange={(e) => onChange(e.target.checked)} /> {f.label}</label>
  );
  if (f.type === "yesno") return (
    <div><p className="text-sm font-semibold">{f.label}</p><div className="mt-1.5"><YesNo value={value} onChange={onChange} testId={id} /></div></div>
  );
  if (f.type === "hazardToggles") return (
    <div>
      <p className="text-sm font-semibold">{f.label}</p>
      <div className="mt-1.5 grid gap-1 rounded-md border border-border p-2">
        {(row?.hazards || []).filter((h) => h.scope !== "local").map((h) => (
          <label key={h.id} className="flex min-h-[40px] items-center gap-2 text-sm">
            <input type="checkbox" data-testid={`${id}-${h.id}`} className="h-4 w-4 accent-amber-500" checked={!(value || []).includes(h.id)}
              onChange={(e) => onChange(e.target.checked ? (value || []).filter((x) => x !== h.id) : [...(value || []), h.id])} /> {h.label}{h.linked ? " (rattaché, réglage commun à ses familles)" : ""}
          </label>
        ))}
      </div>
    </div>
  );
  return (
    <div>
      <label htmlFor={id} className="text-sm font-semibold">{f.label}</label>
      <div className="mt-1.5">
        {f.type === "select" && (
          <select id={id} data-testid={id} className={selectCls} value={value ?? ""} onChange={(e) => onChange(f.numeric ? (e.target.value ? Number(e.target.value) : null) : e.target.value)}>
            <option value="">— Choisir —</option>
            {(f.options || options[f.optionsFrom] || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
        {f.type === "list" && <ListEditor value={value || []} onChange={onChange} testId={id} />}
        {f.type === "identifiers" && <IdentifierEditor value={value || []} onChange={onChange} testId={id} />}
        {f.type === "users" && (
          <div className="grid max-h-60 gap-1 overflow-y-auto rounded-md border border-border p-2">
            {(options[f.optionsFrom] || []).map((o) => (
              <label key={o.value} className="flex min-h-[40px] items-center gap-2 text-sm">
                <input type="checkbox" data-testid={`${id}-${o.value}`} className="h-4 w-4 accent-amber-500" checked={(value || []).includes(o.value)}
                  onChange={(e) => onChange(e.target.checked ? [...(value || []), o.value] : (value || []).filter((x) => x !== o.value))} /> {o.label}
              </label>
            ))}
          </div>
        )}
        {(f.type === "text" || f.type === "number") && (
          <Input id={id} data-testid={id} type={f.type} value={value ?? ""} onChange={(e) => onChange(f.type === "number" ? Number(e.target.value) : e.target.value)} className="h-11" />
        )}
      </div>
    </div>
  );
};

export const CrudSection = ({ cfg, options = {}, onChanged }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [formErr, setFormErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ q: "", hazards: "", levels: "" });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = () => {
    setError("");
    const params = cfg.paged ? { ...filters, page, size: PAGE_SIZE } : undefined;
    api.get(`${cfg.base || "/admin"}/${cfg.resource}`, { params })
      .then((r) => { if (cfg.paged) { setRows(r.data.items); setTotal(r.data.total); } else setRows(r.data); })
      .catch((e) => setError(errMessage(e)));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [cfg.resource, filters, page]);

  const open = (row) => {
    setFormErr("");
    setEditing(row ? { ...row } : { ...cfg.defaults });
  };
  const fields = editing?.scope === "global" ? cfg.globalFields : cfg.fields;
  const submit = async () => {
    setBusy(true);
    setFormErr("");
    const payload = Object.fromEntries(fields.map((f) => [f.name, editing[f.name]]));
    try {
      if (editing.id) await api.put(`${cfg.base || "/admin"}/${cfg.resource}/${editing.id}`, payload);
      else await api.post(`${cfg.base || "/admin"}/${cfg.resource}`, payload);
      toast.success("Modification enregistrée.");
      setEditing(null);
      load();
      onChanged?.();
    } catch (e) { setFormErr(errMessage(e)); } finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(cfg.deletable(r))) return;
    try {
      await api.delete(`${cfg.base || "/admin"}/${cfg.resource}/${r.id}`);
      toast.success("Élément supprimé.");
      load();
      onChanged?.();
    } catch (e) { toast.error(errMessage(e)); }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">{cfg.help}</p>
        <Button data-testid={`admin-${cfg.resource}-add`} onClick={() => open(null)}><Plus className="mr-1 h-4 w-4" /> Ajouter</Button>
      </div>
      {cfg.paged && <MeasureFilters hazards={options.hazards} onChange={(f) => { setPage(1); setFilters(f); }} />}
      {error && <ErrorBox message={error} onRetry={load} />}
      {!rows && !error && <Loading />}
      {rows && !rows.length && <Empty>Aucun élément.</Empty>}
      <div className="space-y-2" data-testid={`admin-${cfg.resource}-list`}>
        {rows?.map((r) => (
          <div key={r.id} data-testid={`admin-row-${r.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-3">
            <div className="min-w-0">
              <p className="font-semibold sm:truncate">{cfg.title(r, options)}</p>
              <p className="text-xs text-muted-foreground sm:truncate">{cfg.subtitle?.(r, options)}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {r.scope && <span data-testid={`admin-scope-${r.id}`} className={`rounded-full border px-2 py-0.5 font-data text-[10px] uppercase tracking-widest ${r.scope === "global" ? "border-sky-500/50 text-sky-500" : "border-amber-500/50 text-amber-500"}`}>{r.scope === "global" ? "Global" : "Propre"}</span>}
              {!r.active && <StatusBadge status="inactive" />}
              <Button variant="outline" size="sm" data-testid={`admin-edit-${r.id}`} onClick={() => open(r)}><Pencil className="mr-1 h-3.5 w-3.5" /> Modifier</Button>
              {cfg.deletable && <Button variant="ghost" size="sm" aria-label="Supprimer" data-testid={`admin-delete-${r.id}`} onClick={() => remove(r)} className="text-red-500 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></Button>}
            </div>
          </div>
        ))}
      </div>
      {cfg.paged && <Pager page={page} total={total} onPage={setPage} />}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="admin-dialog" className="max-h-[90vh] overflow-y-auto border-border bg-card">
          <DialogHeader><DialogTitle className="font-heading text-2xl uppercase">{editing?.scope === "global" ? "Réglage local" : editing?.id ? "Modifier" : "Ajouter"} · {cfg.label}</DialogTitle></DialogHeader>
          {editing?.scope === "global" && <p data-testid="admin-global-note" className="text-sm text-muted-foreground">« {editing.text || editing.name} » est défini par Safe Start pour tous les workplaces : sa définition n'est pas modifiable. Vous pouvez l'utiliser ou non ici{editing.hazards ? ", désactiver ses dangers et y ajouter vos propres dangers" : ""}.</p>}
          {editing && <div className="space-y-4">{fields.map((f) => <FieldInput key={f.name} f={f} value={editing[f.name]} row={editing} options={options} onChange={(v) => setEditing({ ...editing, [f.name]: v })} />)}</div>}
          <FieldError error={formErr} testId="admin-form-error" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button data-testid="admin-save-button" onClick={submit} disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
